import { isEmpty } from "lodash";
import {
  AchievementCampaigns,
  Audience,
  AudienceType,
  CampaignCondition,
  CampaignConditionNewFormat,
  CampaignConditionNewFormatParam,
  CampaignFEValues,
  CampaignLimits,
  CampaignTrigger,
  CampaignTriggers,
  CampaignTriggersUnion,
  CampaignType,
  CampaignTypeUnion,
  CampaignTypes,
  CampaignVisibility,
  ConditionType,
  EventCampaigns,
  ItemCriteriaParam,
  NewFormatConditionType,
  OperatorId,
  SelectedLimitType,
  SelectedSchemaCondition,
  TimeBasedCampaign,
  TransactionCampaigns,
  TriggerStrategyType,
  VisibilityTarget,
} from "types/campaigns";
import { KeyValuePair } from "utilities/objects";
import { RedemptionCodeCampaignTooltip } from "./RedemptionCodeCampaignTooltip";

/**
 * Transform translations array format used on FE on format required for BE (POST & PUT) before sending
 */
export const transformTranslations = (
  translations: { description?: string; locale?: string; name?: string }[],
): {} => {
  if (Array.isArray(translations)) {
    return translations?.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.locale as string]: {
          name: cur.name,
          description: cur.description,
        },
      }),
      {},
    );
  } else {
    return translations;
  }
};

/**
 * Sort translations array from BE - first must be current apiDefault one (locale same as code of apiDefault language)
 */
export const sortCampaignTranslations = (
  defaultLangCode: string,
  translations: { description?: string; locale?: string; name?: string }[],
) => [
  ...translations.filter(({ locale }) => locale === defaultLangCode),
  ...translations.filter(({ locale }) => locale !== defaultLangCode),
];

/**
 * Hardcoded order of params sending to BE as an attribute string
 */
const paramsHardcodedOrder = [
  "productBrand",
  "productName",
  "productCategory",
  "productCustomAttribute",
];

/**
 * Sort params array in hardcoded order and format it to the form acceptable by backend
 */
const sortAndFormatParams = (params?: ItemCriteriaParam[]) => {
  // Create object to store values for every criterion
  const valuesByCriterion: { [key: string]: (string | object)[] } = {};

  // Group values by criterion
  if (params) {
    params.forEach((param) => {
      if (valuesByCriterion[param.criterion]) {
        valuesByCriterion[param.criterion].push(...param.values);
      } else {
        valuesByCriterion[param.criterion] = [...param.values];
      }
    });
  } else return "";

  // Sort values in hardcoded order
  const sortedValues = paramsHardcodedOrder.map((key) => {
    return valuesByCriterion[key] || [];
  });

  // Format values to string acceptable by backend
  const formattedValues = sortedValues.map((innerArray) => {
    return `[${innerArray
      .map((value) => {
        if (typeof value === "object") {
          return JSON.stringify(value);
        } else {
          return `"${value}"`;
        }
      })
      .join(", ")}]`;
  });
  return formattedValues.join(", ");
};

const parseNewCondtionParams = (
  field: string,
  param?: CampaignConditionNewFormatParam,
): CampaignConditionNewFormatParam | undefined => {
  if (isEmpty(param)) return undefined;

  switch (field) {
    case "customer.badges":
    case "referrer.badges":
      return {
        badgeTypeId: param.badgeTypeId,
      };
    case "customer.wallets.activeUnits":
    case "customer.wallets.expiredUnits":
    case "customer.wallets.blockedUnits":
    case "customer.wallets.lockedUnits":
    case "customer.wallets.earnedUnits":
    case "customer.wallets.spentUnits":
    case "referrer.wallets.activeUnits":
    case "referrer.wallets.expiredUnits":
    case "referrer.wallets.blockedUnits":
    case "referrer.wallets.lockedUnits":
    case "referrer.wallets.earnedUnits":
    case "referrer.wallets.spentUnits":
      return { field: param.field, walletTypeCode: param.walletTypeCode };
    default:
      return {
        field: param.field,
      };
  }
};

/**
 * On submit some campaign conditions available only on the transaction triggered campaigns needs to be reformatted
 */
export const transformTransactionAttribute = (
  attribute: string,
  parameter?: string | ItemCriteriaParam[],
): string => {
  const param = parameter && parameter.length > 0 ? parameter : "";
  switch (attribute) {
    case "transaction.sku.qty":
      return `transaction.sku("${param}").qty`;
    case "transaction.sku.grossValue":
      return `transaction.sku("${param}").grossValue`;
    case "transaction.category.qty":
      return `transaction.category("${param}").qty`;
    case "transaction.category.grossValue":
      return `transaction.category("${param}").grossValue`;
    case "transaction.maker.qty":
      return `transaction.maker("${param}").qty`;
    case "transaction.maker.grossValue":
      return `transaction.maker("${param}").grossValue`;
    case "transaction.itemsCriteria.grossValue":
      return `transaction.itemsCriteria(${sortAndFormatParams(
        param as ItemCriteriaParam[],
      )}).grossValue`;
    case "transaction.itemsCriteria.qty":
      return `transaction.itemsCriteria(${sortAndFormatParams(
        param as ItemCriteriaParam[],
      )}).qty`;
    default:
      return attribute;
  }
};

const complexConditionRegex =
  /transaction.sku(.*).grossValue|transaction.sku(.*).qty|transaction.category(.*).qty|transaction.category(.*).grossValue|transaction.maker(.*).qty|transaction.maker(.*).grossValue/;
const complexItemsCriteriaRegex =
  /transaction.itemsCriteria(.*).grossValue|transaction.itemsCriteria(.*).qty/;

/**
 * Prepare condition "attribute" property from BE for FE - if it is a complex type, reformat it
 */
const regExp = /\(['"](.*?)['"]\)/;
const itemsCriteriaRegExp = /\([^)]*\)/;

export const getTransactionAttribute = (attribute: string): string => {
  if (attribute?.match(complexConditionRegex)) {
    return attribute.replace(regExp, "");
  } else if (attribute?.match(complexItemsCriteriaRegex)) {
    return attribute.replace(itemsCriteriaRegExp, "");
  } else return attribute;
};

/// Check if condition is wrapped in single quotes and can be parsed
const isOldSchema = (param?: string) => {
  const paramFormatted = param?.replace(/'/g, '"');
  try {
    return !!JSON.parse(`[${paramFormatted}]`);
  } catch {
    return false;
  }
};

/**
 * Prepare condition "param" property from BE for FE - if it is a complex type, reformat it
 */
export const getTransactionParam = (
  attribute: string,
): string | ItemCriteriaParam[] | undefined => {
  if (attribute?.match(complexConditionRegex)) {
    return regExp.exec(attribute)?.[1];
  } else if (attribute?.match(complexItemsCriteriaRegex)) {
    // Get param from attribute as a string and replace single quotes with double quotes to convert it to an array in the next step

    const param = itemsCriteriaRegExp
      .exec(attribute)?.[0]
      .replace(itemsCriteriaRegExp, (match) =>
        match.slice(1, -1).replace(/^\(/, "[").replace(/\)$/, "]"),
      );

    const isOldSchemaPattern = isOldSchema(param);
    const paramFormatted = isOldSchemaPattern
      ? param?.replace(/'/g, '"')
      : param;
    // Convert param to two-dimensional array
    const paramsArray: (string[] | KeyValuePair[])[] = JSON.parse(
      `[${paramFormatted}]`,
    );

    // Create array of objects with criterion and values keys to prepare valid param property
    const transformedArray: ItemCriteriaParam[] = [];
    paramsArray.forEach((values, index) => {
      values.length &&
        transformedArray.push({
          criterion: paramsHardcodedOrder[index],
          values: values,
        });
    });

    return transformedArray;
  } else return undefined;
};

export const prepareVisibilityData = (visibility?: CampaignVisibility) => {
  const { NONE, SEGMENT, TIER, ALL } = VisibilityTarget;

  if (visibility?.target === NONE) {
    return { target: NONE };
  } else if (visibility?.target === SEGMENT) {
    return {
      target: visibility?.target,
      segments: visibility?.segments,
    };
  } else if (visibility?.target === TIER) {
    return {
      target: visibility?.target,
      tiers: visibility?.tiers,
    };
  } else {
    return { target: ALL };
  }
};

const adjustAudience = (audience?: Audience) => {
  if (!audience) return;

  switch (audience.target) {
    case AudienceType.EVERYONE:
      return {};
    case AudienceType.TIER:
      return {
        audience: {
          target: AudienceType.TIER,
          tiers: audience.tiers
            ? Array.isArray(audience.tiers)
              ? audience.tiers
              : [audience.tiers]
            : undefined,
        },
      };
    case AudienceType.SEGMENT:
      return {
        audience: {
          target: AudienceType.SEGMENT,
          segments: audience.segments
            ? Array.isArray(audience.segments)
              ? audience.segments
              : [audience.segments]
            : undefined,
        },
      };
  }
};

export const isNewFormat = (
  condition:
    | CampaignCondition
    | CampaignConditionNewFormat
    | SelectedSchemaCondition
    | ConditionType,
): condition is CampaignConditionNewFormat => "value" in condition;

export const isOldFormat = (
  condition:
    | CampaignCondition
    | CampaignConditionNewFormat
    | SelectedSchemaCondition
    | ConditionType,
): condition is CampaignCondition | SelectedSchemaCondition | ConditionType =>
  "attribute" in condition;

export const findConditionIdPath = (
  condition:
    | CampaignCondition
    | CampaignConditionNewFormat
    | SelectedSchemaCondition
    | ConditionType,
) => {
  if (isNewFormat(condition)) {
    return condition.value.field;
  } else if (isOldFormat(condition)) {
    return condition.attribute;
  } else return "";
};

const setField = (field: string) => {
  switch (true) {
    case field.includes("transaction.items."):
      return "transaction.items";
    case field.includes("customer.wallets."):
      return "customer.wallets";
    case field.includes("referrer.wallets."):
      return "referrer.wallets";
    default:
      return field;
  }
};

export const parseTimeCampaign = ({
  values,
}: {
  values: TimeBasedCampaign;
}) => {
  const {
    active,
    activity,
    trigger,
    triggerStrategy,
    labels,
    displayOrder,
    translations,
    visibility,
    audience,
    limits,
    rules,
  } = values;

  const isDailyTriggerStrategy =
    triggerStrategy?.type === TriggerStrategyType.DAILY;
  const isWeeklyTriggerStrategy =
    triggerStrategy?.type === TriggerStrategyType.WEEKLY;
  const isMonthlyTriggerStrategy =
    triggerStrategy?.type === TriggerStrategyType.MONTHLY;

  return {
    type: CampaignTypes.DIRECT,
    trigger,
    activity,
    active,
    labels,
    displayOrder,
    ...(isDailyTriggerStrategy ||
    isWeeklyTriggerStrategy ||
    isMonthlyTriggerStrategy
      ? adjustAudience(audience)
      : {}),
    memberFilter: { strategy: "birthday" },
    translations: transformTranslations(translations),
    visibility:
      visibility?.target === VisibilityTarget.ALL ? undefined : visibility,
    limits: {
      executions: prepareFECampaignLimitFormatForBE(limits?.executions),
      executionsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.executionsPerMember,
      ),
      points: prepareFECampaignLimitFormatForBE(limits?.points),
      pointsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.pointsPerMember,
      ),
    },
    triggerStrategy: {
      type: triggerStrategy?.type,
      executionSchedule:
        isWeeklyTriggerStrategy || isMonthlyTriggerStrategy
          ? {
              ...(isWeeklyTriggerStrategy
                ? {
                    dayOfWeek: triggerStrategy?.executionSchedule?.dayOfWeek,
                  }
                : {}),
              ...(isMonthlyTriggerStrategy
                ? {
                    dayOfMonth: triggerStrategy?.executionSchedule?.dayOfMonth,
                  }
                : {}),
            }
          : undefined,
    },
    rules: rules.map(({ conditions, effects, name, description }) => {
      return {
        name,
        description,
        conditions: conditions?.map((condition) => {
          return isNewFormat(condition)
            ? {
                value: {
                  field: setField(condition.value.field),
                  params: parseNewCondtionParams(
                    condition.value.field,
                    condition.value.params,
                  ),
                },
                condition: condition.condition,
              }
            : {
                data: condition.data,
                name: condition.name,
                operator: condition.operator,
                description: condition.description,
                attribute:
                  condition.attribute === "expression"
                    ? undefined
                    : condition.attribute,
              };
        }),
        effects: effects.map(
          ({
            couponValueRule,
            customAttributeKey,
            customAttributeValueRule,
            effect,
            pointsRule,
            rewardId,
            walletCode,
            unitsExpirationRule,
            unitsLockRule,
          }) => {
            switch (effect) {
              case "give_points":
                const noExpirationExpression =
                  unitsExpirationRule?.expirationStrategy === "expression" &&
                  !unitsExpirationRule.expression;
                const noPendingExpression =
                  unitsLockRule?.lockStrategy === "expression" &&
                  !unitsLockRule.expression;

                return {
                  effect,
                  pointsRule,
                  walletCode,
                  unitsExpirationRule: noExpirationExpression
                    ? undefined
                    : {
                        expirationStrategy:
                          unitsExpirationRule?.expirationStrategy,
                        expression: unitsExpirationRule?.expression,
                      },
                  unitsLockRule: noPendingExpression
                    ? undefined
                    : {
                        lockStrategy: unitsLockRule?.lockStrategy,
                        expression: unitsLockRule?.expression,
                      },
                };
              case "deduct_unit":
                return {
                  effect,
                  pointsRule,
                  walletCode,
                };
              case "assign_member_custom_attribute":
              case "remove_member_custom_attribute":
                return {
                  customAttributeKey,
                  customAttributeValueRule,
                  effect,
                };
              default:
                return {
                  effect,
                  rewardId: rewardId,
                  couponValueRule: couponValueRule,
                };
            }
          },
        ),
      };
    }),
  };
};

const parseAchievementCampaign = ({
  values,
}: {
  values: AchievementCampaigns;
}) => {
  const {
    type,
    trigger,
    active,
    activity,
    labels,
    displayOrder,
    achievementId,
    limits,
    visibility,
    translations,
    rules,
  } = values;

  return {
    type,
    trigger,
    active,
    labels,
    activity,
    displayOrder,
    achievementId,
    translations: transformTranslations(translations),
    multiLevel: "multiLevel" in values ? values.multiLevel : undefined,
    visibility:
      visibility?.target === VisibilityTarget.ALL ? undefined : visibility,
    limits: {
      executions: prepareFECampaignLimitFormatForBE(limits?.executions),
      executionsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.executionsPerMember,
      ),
      points: prepareFECampaignLimitFormatForBE(limits?.points),
      pointsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.pointsPerMember,
      ),
    },
    rules: Array.from(rules)?.map(
      ({ conditions, effects, name, description, ...rule }) => {
        return {
          // i.e. rule target ("self" or "referrer") if this is a "referral" type campaign
          name,
          description,
          target: "target" in rule ? rule.target : undefined,
          conditions: conditions?.map((condition) => {
            return isNewFormat(condition)
              ? {
                  value: {
                    field: setField(condition.value.field),
                    params: parseNewCondtionParams(
                      condition.value.field,
                      condition.value.params,
                    ),
                  },
                  condition: condition.condition,
                }
              : {
                  data: condition.data,
                  name: condition.name,
                  operator: condition.operator,
                  description: condition.description,
                  attribute:
                    condition.attribute === "expression"
                      ? undefined
                      : transformTransactionAttribute(
                          condition.attribute,
                          condition.param,
                        ),
                };
          }),
          effects: effects?.map(
            ({
              couponValueRule,
              customAttributeKey,
              customAttributeValueRule,
              effect,
              pointsRule,
              rewardId,
              walletCode,
              unitsExpirationRule,
              unitsLockRule,
            }) => {
              switch (effect) {
                case "give_points":
                  const noExpirationExpression =
                    unitsExpirationRule?.expirationStrategy === "expression" &&
                    !unitsExpirationRule.expression;
                  const noPendingExpression =
                    unitsLockRule?.lockStrategy === "expression" &&
                    !unitsLockRule.expression;

                  return {
                    effect,
                    pointsRule,
                    walletCode,
                    unitsExpirationRule: noExpirationExpression
                      ? undefined
                      : {
                          expirationStrategy:
                            unitsExpirationRule?.expirationStrategy,
                          expression: unitsExpirationRule?.expression,
                        },
                    unitsLockRule: noPendingExpression
                      ? undefined
                      : {
                          lockStrategy: unitsLockRule?.lockStrategy,
                          expression: unitsLockRule?.expression,
                        },
                  };
                case "deduct_unit":
                  return {
                    effect,
                    pointsRule,
                    walletCode,
                  };
                case "assign_member_custom_attribute":
                case "remove_member_custom_attribute":
                  return {
                    customAttributeKey,
                    customAttributeValueRule,
                    effect,
                  };
                default:
                  return {
                    effect,
                    rewardId: rewardId,
                    couponValueRule: couponValueRule,
                  };
              }
            },
          ),
        };
      },
    ),
  };
};

export const parseTransactionCampaign = ({
  values,
}: {
  values: TransactionCampaigns;
}) => {
  const {
    type,
    trigger,
    active,
    labels,
    displayOrder,
    activity,
    transactionItemsFilters,
    rules,
    limits,
    visibility,
    translations,
  } = values;

  return {
    type,
    trigger,
    active,
    labels,
    activity,
    displayOrder,
    translations: transformTranslations(translations),
    multiLevel: "multiLevel" in values ? values.multiLevel : undefined,
    visibility:
      visibility?.target === VisibilityTarget.ALL ? undefined : visibility,
    limits: {
      executions: prepareFECampaignLimitFormatForBE(limits?.executions),
      executionsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.executionsPerMember,
      ),
      points: prepareFECampaignLimitFormatForBE(limits?.points),
      pointsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.pointsPerMember,
      ),
    },
    transactionItemsFilters: transactionItemsFilters?.map(
      ({ name, code, filters }) => ({
        name,
        code,
        filters: filters.map(({ condition, value }) => ({
          condition,
          value,
        })),
      }),
    ),
    rules: Array.from(rules)?.map(
      ({ conditions, effects, name, description, ...rule }) => {
        return {
          // i.e. rule target ("self" or "referrer") if this is a "referral" type campaign
          name,
          description,
          target: "target" in rule ? rule.target : undefined,
          conditions: conditions?.map((condition) => {
            return isNewFormat(condition)
              ? {
                  value: {
                    field: setField(condition.value.field),
                    aggregation: condition.value.aggregation,
                    filterCode: condition.value.filterCode,
                    params: parseNewCondtionParams(
                      condition.value.field,
                      condition.value.params,
                    ),
                  },
                  condition: condition.condition,
                }
              : {
                  data: condition.data,
                  name: condition.name,
                  operator: condition.operator,
                  description: condition.description,
                  attribute:
                    condition.attribute === "expression"
                      ? undefined
                      : transformTransactionAttribute(
                          condition.attribute,
                          condition.param,
                        ),
                };
          }),
          effects: effects?.map(
            ({
              couponValueRule,
              customAttributeKey,
              customAttributeValueRule,
              effect,
              pointsRule,
              rewardId,
              walletCode,
              unitsExpirationRule,
              unitsLockRule,
            }) => {
              switch (effect) {
                case "give_points":
                  const noExpirationExpression =
                    unitsExpirationRule?.expirationStrategy === "expression" &&
                    !unitsExpirationRule.expression;
                  const noPendingExpression =
                    unitsLockRule?.lockStrategy === "expression" &&
                    !unitsLockRule.expression;

                  return {
                    effect,
                    pointsRule,
                    walletCode,
                    unitsExpirationRule: noExpirationExpression
                      ? undefined
                      : {
                          expirationStrategy:
                            unitsExpirationRule?.expirationStrategy,
                          expression: unitsExpirationRule?.expression,
                        },
                    unitsLockRule: noPendingExpression
                      ? undefined
                      : {
                          lockStrategy: unitsLockRule?.lockStrategy,
                          expression: unitsLockRule?.expression,
                        },
                  };
                case "deduct_unit":
                  return {
                    effect,
                    pointsRule,
                    walletCode,
                  };
                case "assign_member_custom_attribute":
                case "remove_member_custom_attribute":
                  return {
                    customAttributeKey,
                    customAttributeValueRule,
                    effect,
                  };
                default:
                  return {
                    effect,
                    rewardId: rewardId,
                    couponValueRule: couponValueRule,
                  };
              }
            },
          ),
        };
      },
    ),
  };
};

// Event trigger campaigns

/**
 * Prepare values in add or edit campaign (with trigger "custom_event" or "internal_event") form before endpoint sending
 */
export const parseEventCampaign = ({ values }: { values: EventCampaigns }) => {
  const {
    type,
    trigger,
    active,
    event,
    labels,
    activity,
    displayOrder,
    visibility,
    limits,
    rules,
    translations,
  } = values;

  return {
    type,
    trigger,
    active,
    labels,
    event,
    activity,
    displayOrder,
    eventCodeAttribute:
      "eventCodeAttribute" in values ? values.eventCodeAttribute : undefined,
    codeGenerator: "codeGenerator" in values ? values.codeGenerator : undefined,
    generateCodes: "generateCodes" in values ? values.generateCodes : undefined,
    multiLevel: "multiLevel" in values ? values.multiLevel : undefined,
    translations: transformTranslations(translations),
    visibility:
      visibility?.target === VisibilityTarget.ALL ? undefined : visibility,
    limits: {
      executions: prepareFECampaignLimitFormatForBE(limits?.executions),
      executionsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.executionsPerMember,
      ),
      points: prepareFECampaignLimitFormatForBE(limits?.points),
      pointsPerMember: prepareFECampaignLimitFormatForBE(
        limits?.pointsPerMember,
      ),
    },
    rules: Array.from(rules)?.map(
      ({ conditions, effects, name, description, ...rule }) => {
        return {
          name,
          description,
          target: "target" in rule ? rule.target : undefined,
          conditions: conditions?.map((condition) => {
            return isNewFormat(condition)
              ? {
                  value: {
                    field: setField(condition.value.field),
                    aggregation: condition.value.aggregation,
                    filterCode: condition.value.filterCode,
                    params: parseNewCondtionParams(
                      condition.value.field,
                      condition.value.params,
                    ),
                  },
                  condition: condition.condition,
                }
              : {
                  data: condition.data,
                  name: condition.name,
                  operator: condition.operator,
                  description: condition.description,
                  attribute:
                    condition.attribute === "expression"
                      ? undefined
                      : condition.attribute,
                };
          }),
          effects: effects?.map(
            ({
              couponValueRule,
              customAttributeKey,
              customAttributeValueRule,
              effect,
              pointsRule,
              rewardId,
              walletCode,
              unitsExpirationRule,
              unitsLockRule,
            }) => {
              switch (effect) {
                case "give_points":
                  const noExpirationExpression =
                    unitsExpirationRule?.expirationStrategy === "expression" &&
                    !unitsExpirationRule.expression;
                  const noPendingExpression =
                    unitsLockRule?.lockStrategy === "expression" &&
                    !unitsLockRule.expression;

                  return {
                    effect,
                    pointsRule,
                    walletCode,
                    unitsExpirationRule: noExpirationExpression
                      ? undefined
                      : {
                          expirationStrategy:
                            unitsExpirationRule?.expirationStrategy,
                          expression: unitsExpirationRule?.expression,
                        },
                    unitsLockRule: noPendingExpression
                      ? undefined
                      : {
                          lockStrategy: unitsLockRule?.lockStrategy,
                          expression: unitsLockRule?.expression,
                        },
                  };
                case "deduct_unit":
                  return {
                    effect,
                    pointsRule,
                    walletCode,
                  };
                case "assign_member_custom_attribute":
                case "remove_member_custom_attribute":
                  return {
                    customAttributeKey,
                    customAttributeValueRule,
                    effect,
                  };
                default:
                  return {
                    effect,
                    rewardId: rewardId,
                    couponValueRule: couponValueRule,
                  };
              }
            },
          ),
        };
      },
    ),
  };
};

export const prepareBECampaignLimitFormatForFE = (limits?: CampaignLimits) =>
  limits
    ? {
        executions: prepareSingleBECampaignLimitFormatForFE(limits?.executions),
        executionsPerMember: prepareSingleBECampaignLimitFormatForFE(
          limits?.executionsPerMember,
        ),
        points: prepareSingleBECampaignLimitFormatForFE(limits?.points),
        pointsPerMember: prepareSingleBECampaignLimitFormatForFE(
          limits?.pointsPerMember,
        ),
      }
    : undefined;

const prepareSingleBECampaignLimitFormatForFE = (
  limits?: SelectedLimitType,
) => {
  if (!limits) {
    return { interval: { type: "noLimit" } };
  } else if (!limits.interval) {
    return { ...limits, interval: { type: "inTotal" } };
  } else return limits;
};

export const prepareFECampaignLimitFormatForBE = (
  limits?: SelectedLimitType,
) => {
  if (limits?.interval.type === "noLimit") {
    return undefined;
  } else if (limits?.interval.type === "inTotal") {
    return { ...limits, interval: undefined };
  } else return { ...limits, interval: { ...limits?.interval, value: 1 } };
};

export const campaignTypes: CampaignType[] = [
  {
    campaign: CampaignTypes.DIRECT,
    descriptionText: "directCampaignInfo",
    label: "directCampaign",
  },
  {
    campaign: CampaignTypes.REFERRAL,
    descriptionText: "referralCampaignInfo",
    label: "referralCampaign",
  },
];

export const campaignTriggers: CampaignTrigger[] = [
  {
    campaign: CampaignTriggers.TRANSACTION,
    label: "purchaseTransaction",
    descriptionText: "purchaseTransactionMatchedWithMember",
  },
  {
    campaign: CampaignTriggers.RETURN_TRANSACTION,
    label: "returnTransaction",
    descriptionText: "returnTransactionMatchedWithMember",
  },
  {
    campaign: CampaignTriggers.INTERNAL_EVENT,
    label: "internalEvent",
    descriptionText: "eventTrackedOnMemberBehaviour",
  },
  {
    campaign: CampaignTriggers.CUSTOM_EVENT,
    label: "customEvent",
    descriptionText: "eventSourcedFromExternalSystemOrApp",
  },
  {
    campaign: CampaignTriggers.ACHIEVEMENT,
    label: "achievement",
    descriptionText: "achivementCompletedByMember",
  },
  {
    campaign: CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE,
    label: "redemptionCode",
    descriptionText: "eventBasedOnRedemptionCodes",
    tooltip: <RedemptionCodeCampaignTooltip />,
  },
];

export const campaignSimulationTriggers: CampaignTrigger[] = [
  {
    campaign: CampaignTriggers.TRANSACTION,
    label: "transaction",
  },
  {
    campaign: CampaignTriggers.RETURN_TRANSACTION,
    label: "returnTransaction",
    descriptionText: "returnTransactionMatchedWithMember",
  },
  {
    campaign: CampaignTriggers.INTERNAL_EVENT,
    label: "internalEvent",
  },
  {
    campaign: CampaignTriggers.CUSTOM_EVENT,
    label: "customEvent",
  },
];

export const referralCustomerConditions: CampaignCondition[] = [
  {
    attribute: "referrer.address.address1",
    label: "referrerBuildingName",
    type: "text",
  },
  { attribute: "referrer.address.city", label: "referrerCity", type: "text" },
  {
    attribute: "referrer.address.country",
    label: "referrerCountry",
    type: "countryDropdown",
  },
  {
    attribute: "referrer.dataProcessingConsent",
    data: false,
    label: "referrerDataProcessingConsent",
    operator: "is_equal",
    type: "boolean",
  },
  {
    attribute: "referrer.birthDate",
    data: null,
    label: "referrerDateOfBirth",
    type: "date",
  },
  { attribute: "referrer.email", label: "referrerEmail", type: "text" },
  { attribute: "referrer.firstName", label: "referrerFirstName", type: "text" },
  {
    attribute: "referrer.address.address2",
    label: "referrerFlatUnitName",
    type: "text",
  },
  {
    attribute: "referrer.gender",
    label: "referrerGender",
    type: "genderDropdown",
  },
  { attribute: "referrer.lastName", label: "referrerLastName", type: "text" },
  {
    attribute: "referrer.legalConsent",
    data: false,
    label: "referrerLegalConsent",
    operator: "is_equal",
    type: "boolean",
  },
  {
    attribute: "referrer.loyaltyCardNumber",
    label: "referrerLoyaltyCardNumber",
    type: "text",
  },
  {
    attribute: "referrer.marketingConsent",
    data: false,
    label: "referrerMarketingConsent",
    operator: "is_equal",
    type: "boolean",
  },
  {
    attribute: "referrer.labels",
    data: [],
    label: "referrerCustomAttributes",
    type: "label",
  },
  {
    attribute: "referrer.segments",
    label: "referrerSegments",
    type: "segmentDropdown",
  },
  { attribute: "referrer.phone", label: "referrerPhoneNumber", type: "text" },
  {
    attribute: "referrer.address.postal",
    label: "referrerPostalCode",
    type: "text",
  },
  {
    attribute: "referrer.address.province",
    label: "referrerProvince",
    type: "text",
  },
  {
    attribute: "referrer.registeredDate",
    data: null,
    label: "referrerDateOfRegistration",
    type: "datetime",
  },
  {
    attribute: "referrer.address.street",
    label: "referrerStreet",
    type: "text",
  },
  {
    attribute: "referrer.levelId",
    label: "referrerTier",
    type: "tierDropdown",
  },
  {
    attribute: "referrer.tiers",
    label: "referrerTiers",
    type: "tierDropdown",
  },
  {
    attribute: "referrer.levelAchievementDate",
    data: null,
    label: "referrerTierReachedDate",
    type: "datetime",
  },
  {
    attribute: "customer.address.address1",
    label: "refereeBuildingName",
    type: "text",
  },
  { attribute: "customer.address.city", label: "refereeCity", type: "text" },
  {
    attribute: "customer.address.country",
    label: "refereeCountry",
    type: "countryDropdown",
  },
  {
    attribute: "customer.dataProcessingConsent",
    data: false,
    label: "refereeDataProcessingConsent",
    operator: "is_equal",
    type: "boolean",
  },
  {
    attribute: "customer.birthDate",
    data: null,
    label: "refereeDateOfBirth",
    type: "date",
  },
  { attribute: "customer.email", label: "refereeEmail", type: "text" },
  { attribute: "customer.firstName", label: "refereeFirstName", type: "text" },
  {
    attribute: "customer.address.address2",
    label: "refereeFlatUnitName",
    type: "text",
  },
  {
    attribute: "customer.gender",
    label: "refereeGender",
    type: "genderDropdown",
  },
  { attribute: "customer.lastName", label: "refereeLastName", type: "text" },
  {
    attribute: "customer.labels",
    data: [],
    label: "refereeCustomAttributes",
    type: "label",
  },
  {
    attribute: "customer.segments",
    label: "refereeSegments",
    type: "segmentDropdown",
  },
  { attribute: "customer.phone", label: "refereePhoneNumber", type: "text" },
  {
    attribute: "customer.address.postal",
    label: "refereePostalCode",
    type: "text",
  },
  {
    attribute: "customer.address.province",
    label: "refereeProvince",
    type: "text",
  },
  {
    attribute: "customer.registeredDate",
    data: null,
    label: "refereeDateOfRegistration",
    type: "datetime",
  },
  {
    attribute: "customer.address.street",
    label: "refereeStreet",
    type: "text",
  },
  {
    attribute: "customer.levelId",
    label: "refereeTierFromDefaultTierset",
    type: "tierDropdown",
  },
  {
    attribute: "customer.tiers",
    label: "refereeTiers",
    type: "tierDropdown",
  },
  {
    attribute: "customer.levelAchievementDate",
    data: null,
    label: "refereeTierReachedDate",
    type: "datetime",
  },
];

export const campaignEffects = [
  { effect: "assign_member_custom_attribute" },
  { effect: "deduct_unit" },
  { effect: "give_points" },
  { effect: "give_reward" },
  { effect: "remove_member_custom_attribute" },
];

export const operators = {
  [NewFormatConditionType.STRING]: [
    { id: OperatorId.EQUAL, label: "isEqual" },
    { id: OperatorId.NOT_EQUAL, label: "isNotEqual" },
    { id: OperatorId.STARTS_WITH, label: "startsWith" },
    { id: OperatorId.ENDS_WITH, label: "endsWith" },
    { id: OperatorId.MATCH, label: "matchesRegex" },
    { id: OperatorId.NOT_MATCH, label: "notMatchesRegex" },
    { id: OperatorId.IN, label: "in" },
    { id: OperatorId.NOT_IN, label: "notIn" },
    { id: OperatorId.IN_VALUE_GROUP, label: "isInCollections" },
    { id: OperatorId.NOT_IN_VALUE_GROUP, label: "isNotInCollections" },
  ],
  [NewFormatConditionType.NUMBER]: [
    { id: OperatorId.EQUAL, label: "isEqual" },
    { id: OperatorId.NOT_EQUAL, label: "isNotEqual" },
    { id: OperatorId.GREATER, label: "isGreaterThan" },
    { id: OperatorId.GREATER_OR_EQUAL, label: "isGreaterOrEqual" },
    { id: OperatorId.LESS, label: "isLessThan" },
    { id: OperatorId.LESS_OR_EQUAL, label: "isLessOrEqual" },
    { id: OperatorId.IN, label: "in" },
    { id: OperatorId.NOT_IN, label: "notIn" },
    { id: OperatorId.BETWEEN, label: "isBetween" },
    { id: OperatorId.NOT_BETWEEN, label: "isNotBetween" },
  ],
  [NewFormatConditionType.LABELS_ARRAY]: [
    { id: OperatorId.ONE_OF, label: "isOneOf" },
    { id: OperatorId.NOT_ONE_OF, label: "isNotOneOf" },
    { id: OperatorId.ONE_OF_VALUE_GROUP, label: "isOneOfCollections" },
    { id: OperatorId.NOT_ONE_OF_VALUE_GROUP, label: "isNotOneOfCollections" },
  ],
};

export const formatSubmitValues = ({
  values,
}: {
  values: CampaignFEValues;
}) => {
  switch (values.trigger) {
    case CampaignTriggers.TRANSACTION:
    case CampaignTriggers.RETURN_TRANSACTION:
      return parseTransactionCampaign({ values });
    case CampaignTriggers.ACHIEVEMENT:
      return parseAchievementCampaign({ values });
    case CampaignTriggers.CUSTOM_EVENT:
    case CampaignTriggers.INTERNAL_EVENT:
    case CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE:
      return parseEventCampaign({
        values,
      });
    case CampaignTriggers.TIME:
      return parseTimeCampaign({ values });
  }
};

export const isAchievementTrigger = (trigger: CampaignTriggersUnion) =>
  trigger === CampaignTriggers.ACHIEVEMENT;

export const isInternalEventTrigger = (trigger: CampaignTriggersUnion) =>
  trigger === CampaignTriggers.INTERNAL_EVENT;

export const isRedemptionCodeTrigger = (trigger: CampaignTriggersUnion) =>
  trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE;

export const isEventType = (trigger: CampaignTriggersUnion) =>
  trigger === CampaignTriggers.CUSTOM_EVENT ||
  trigger === CampaignTriggers.INTERNAL_EVENT ||
  trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE;

export const isReferralType = (type: CampaignTypeUnion) =>
  type === CampaignTypes.REFERRAL;

export const timeBasedTriggers = [
  {
    attribute: TriggerStrategyType.DAILY,
    label: "daily",
    description: "automationTriggeredOnceEveryDay",
  },
  {
    attribute: TriggerStrategyType.WEEKLY,
    label: "weekly",
    description: "automationTriggeredEveryWeekOnSelectedDays",
  },
  {
    attribute: TriggerStrategyType.MONTHLY,
    label: "monthly",
    description: "automationTriggeredEveryMonthOnSelectedDays",
  },
  {
    attribute: TriggerStrategyType.BIRTHDAY,
    label: "membersBirthday",
    description: "automationTriggeredOnMembersBirthday",
  },
  {
    attribute: TriggerStrategyType.REGISTRATION_ANNIVERSARY,
    label: "membershipAnniversary",
    description: "automationTriggeredOnMembersRegistrationDate",
  },
];

export const prepareExportCampaignsJson = (campaigns: any[]) => {
  return campaigns.map((campaign) => ({
    active: campaign.active,
    activity: {
      startsAt: campaign.activity.startsAt as string,
      endsAt: campaign.activity.endsAt || null,
    },
    translations: campaign.translations,
    labels: campaign.labels || [],
    limits: prepareBECampaignLimitFormatForFE(campaign.limits),
    rules: campaign.rules,
    transactionItemsFilters: campaign.transactionItemsFilters,
    displayOrder: campaign.displayOrder,
    type:
      campaign.trigger === CampaignTriggers.TIME
        ? CampaignTypes.TIME
        : campaign.type,
    trigger: campaign.trigger,
    ...(isReferralType(campaign.type) && {
      multiLevel: campaign.multiLevel,
    }),
    ...(isRedemptionCodeTrigger(campaign.trigger) && {
      eventCodeAttribute: campaign.eventCodeAttribute,
      generateCodes: campaign.generateCodes,
      codeGenerator: campaign.codeGenerator,
    }),
    ...(isEventType(campaign.trigger) && {
      event: campaign?.event,
    }),
    ...(isAchievementTrigger(campaign.trigger) && {
      achievementId: campaign.achievementId,
    }),
    visibility: prepareVisibilityData(campaign.visibility),
    name: campaign.name,
    ...(campaign?.triggerStrategy?.type && {
      audience: campaign?.audience
        ? campaign.audience
        : { target: AudienceType.EVERYONE },
    }),
    ...(campaign?.triggerStrategy && {
      triggerStrategy:
        campaign.trigger === CampaignTypes.TIME &&
        !campaign.triggerStrategy.type
          ? { type: TriggerStrategyType.BIRTHDAY }
          : campaign.triggerStrategy,
    }),
  }));
};

type DaysTrigger = number[] | "L"[];

export const parseMonthlyDays = ({
  days,
  message,
}: {
  days?: DaysTrigger;
  message: any;
}) => {
  if (!days) return "";
  const isLastDayOfMonth: boolean = !!days.find((day) => day === "L");

  if (isLastDayOfMonth) {
    return message({ id: "lastDayOfTheMonth" });
  }
  const isMoreThanOneDay = days.length > 1;
  return message(
    { id: isMoreThanOneDay ? "daysOfEachMonth" : "dayOfEachMonth" },
    { days: days.join(", ") },
  );
};
